import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ContentSection {
  type: 'text' | 'image' | 'list' | 'highlight';
  content: string | string[];
  alt?: string;
}

interface ModuleContentProps {
  title: string;
  description: string;
  sections: ContentSection[];
  icon: string;
}

const ModuleContent = ({ title, description, sections, icon }: ModuleContentProps) => {
  return (
    <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-natural">
      <div className="flex items-start space-x-4 mb-6">
        <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          <Icon name={icon as any} size={24} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-2">
            {title}
          </h2>
          <p className="text-text-secondary leading-relaxed">{description}</p>
        </div>
      </div>
      
      <div className="space-y-6">
        {sections.map((section, index) => {
          if (section.type === 'text') {
            return (
              <p key={index} className="text-text-primary leading-relaxed">
                {section.content as string}
              </p>
            );
          }
          
          if (section.type === 'image') {
            return (
              <div key={index} className="rounded-xl overflow-hidden shadow-natural">
                <AppImage
                  src={section.content as string}
                  alt={section.alt || 'Educational diagram showing carbon credit concepts'}
                  className="w-full h-64 lg:h-80 object-cover"
                />
              </div>
            );
          }
          
          if (section.type === 'list') {
            return (
              <ul key={index} className="space-y-3">
                {(section.content as string[]).map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-conversion flex-shrink-0 mt-0.5" />
                    <span className="text-text-primary">{item}</span>
                  </li>
                ))}
              </ul>
            );
          }
          
          if (section.type === 'highlight') {
            return (
              <div key={index} className="bg-accent/10 border-l-4 border-accent rounded-r-lg p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="LightBulbIcon" size={24} className="text-accent flex-shrink-0" />
                  <p className="text-text-primary font-medium">{section.content as string}</p>
                </div>
              </div>
            );
          }
          
          return null;
        })}
      </div>
    </div>
  );
};

export default ModuleContent;